import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const EMPLOYEE_VIEW_PERMISSION_KEYS = {
  allowEmployeeRecordSales: "employee_record_sales",
  allowEmployeeViewSalesHistory: "employee_view_sales_history",
  allowEmployeeRecordExpenses: "employee_record_expenses",
  allowEmployeeTransportLogistics: "employee_transport_logistics",
  allowEmployeeAddInventoryItems: "employee_add_inventory_items",
  allowEmployeeAdjustInventory: "employee_adjust_inventory",
  allowEmployeeExportInventory: "employee_export_inventory",
  allowEmployeeDeleteSale: "allow_employee_delete_sale",
} as const;

export type EmployeeViewSettingKey = keyof typeof EMPLOYEE_VIEW_PERMISSION_KEYS;

export type EmployeeViewSettings = Record<EmployeeViewSettingKey, boolean>;

export const DEFAULT_EMPLOYEE_VIEW_SETTINGS: EmployeeViewSettings = {
  allowEmployeeRecordSales: true,
  allowEmployeeViewSalesHistory: true,
  allowEmployeeRecordExpenses: true,
  allowEmployeeTransportLogistics: true,
  allowEmployeeAddInventoryItems: true,
  allowEmployeeAdjustInventory: true,
  allowEmployeeExportInventory: true,
  allowEmployeeDeleteSale: false,
};

const permissionKeyToSetting = Object.fromEntries(
  Object.entries(EMPLOYEE_VIEW_PERMISSION_KEYS).map(([settingKey, permissionKey]) => [permissionKey, settingKey]),
) as Record<(typeof EMPLOYEE_VIEW_PERMISSION_KEYS)[EmployeeViewSettingKey], EmployeeViewSettingKey>;

export function useEmployeeViewSettings() {
  const { businessId, profile } = useAuth();
  const resolvedBusinessId = businessId ?? profile?.business_id ?? null;
  const [settings, setSettings] = useState<EmployeeViewSettings>(DEFAULT_EMPLOYEE_VIEW_SETTINGS);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!resolvedBusinessId) {
      setSettings(DEFAULT_EMPLOYEE_VIEW_SETTINGS);
      setLoading(false);
      return;
    }

    setLoading(true);
    const permissionKeys = Object.values(EMPLOYEE_VIEW_PERMISSION_KEYS);
    const { data, error } = await supabase
      .from("permission_overrides")
      .select("permission_key, enabled")
      .eq("business_id", resolvedBusinessId)
      .in("permission_key", permissionKeys);

    if (error) {
      setSettings(DEFAULT_EMPLOYEE_VIEW_SETTINGS);
      setLoading(false);
      return;
    }

    const nextSettings = { ...DEFAULT_EMPLOYEE_VIEW_SETTINGS };
    (data ?? []).forEach((row) => {
      const settingKey = permissionKeyToSetting[row.permission_key as keyof typeof permissionKeyToSetting];
      if (settingKey) nextSettings[settingKey] = row.enabled ?? true;
    });

    setSettings(nextSettings);
    setLoading(false);
  }, [resolvedBusinessId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSetting = useCallback(
    async (settingKey: EmployeeViewSettingKey, enabled: boolean) => {
      if (!resolvedBusinessId) return;

      const previous = settings[settingKey];
      setSettings((current) => ({ ...current, [settingKey]: enabled }));

      const { error } = await supabase.from("permission_overrides").upsert(
        {
          business_id: resolvedBusinessId,
          permission_key: EMPLOYEE_VIEW_PERMISSION_KEYS[settingKey],
          enabled,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "business_id,permission_key" },
      );

      if (error) {
        setSettings((current) => ({ ...current, [settingKey]: previous }));
        throw error;
      }
    },
    [resolvedBusinessId, settings],
  );

  return {
    settings,
    loading,
    saveSetting,
    refetch: fetchSettings,
  };
}
